import React from 'react';
import { DEG } from './warmupShape';

/**
 * The pattern picture — pitch height against time. Ported from the prototype's
 * `patternSVG()`, with one deliberate change of technique: it built an HTML
 * string and injected it, and this returns real JSX. Same geometry, same
 * shapes, no `dangerouslySetInnerHTML` — the catalog is seeded from a file
 * rather than typed by a user, but "trusted today" is not a reason to keep an
 * injection path on a page.
 *
 * Colours come from tokens rather than the prototype's literals (#1D2438,
 * #DCD9CE, #B8BCCB): those were picked against the prototype's own cream
 * background and would sit wrong in the portal's themes, Comfort-Dark most of
 * all. `currentColor` on the note marks means the picture follows its row.
 *
 * Three step kinds, as in the prototype:
 *   "R"  a rest — consumes time, draws nothing
 *   "X"  an unpitched tick — a flat bar at mid height
 *   any DEG key — a note, drawn as a bar, or a sloped line into the next note
 *                 when the pair is listed in `glides`
 */

const W = 320;
const H = 64;
const PAD = 6;

const WarmupPattern = ({ exercise }) => {
	const notes = exercise?.pattern || [];
	if (!notes.length) return null;

	const total = notes.reduce((a, n) => a + n[1], 0) || 1;
	const pitched = notes.map((n) => DEG[n[0]]).filter((v) => v !== undefined);
	const lo = Math.min(0, ...pitched);
	const hi = Math.max(12, ...pitched);
	const y = (v) => H - PAD - ((v - lo) / (hi - lo || 1)) * (H - 2 * PAD);

	const glides = new Set((exercise.glides || []).map((g) => `${g[0]}-${g[1]}`));
	const marks = [];
	let x = PAD;

	notes.forEach((n, i) => {
		const w = (n[1] / total) * (W - 2 * PAD);
		if (n[0] === 'R') {
			x += w;
			return;
		}
		if (n[0] === 'X') {
			marks.push(
				<rect
					key={`x-${i}`}
					x={x + 1}
					y={H / 2 - 2}
					width={Math.max(2, w - 2)}
					height={4}
					rx={2}
					className="wu-pat__tick"
				/>
			);
			x += w;
			return;
		}
		const cy = y(DEG[n[0]]);
		const next = notes[i + 1];
		if (next && glides.has(`${i}-${i + 1}`) && DEG[next[0]] !== undefined) {
			marks.push(
				<path
					key={`g-${i}`}
					d={`M${x + 2} ${cy} L${x + w} ${y(DEG[next[0]])}`}
					strokeWidth={2.5}
					fill="none"
					strokeLinecap="round"
					className="wu-pat__note"
				/>
			);
		} else {
			marks.push(
				<rect
					key={`n-${i}`}
					x={x + 1}
					y={cy - 2.5}
					width={Math.max(3, w - 2)}
					height={5}
					rx={2.5}
					className="wu-pat__note"
				/>
			);
		}
		x += w;
	});

	return (
		<svg
			className="wu-pat"
			viewBox={`0 0 ${W} ${H}`}
			preserveAspectRatio="none"
			role="img"
			aria-label={`Pattern: ${notes.map((n) => n[0]).join(' ')}`}>
			{/* Tonic and octave guides, so height reads as pitch rather than as decoration. */}
			<line x1="0" x2={W} y1={y(0)} y2={y(0)} strokeDasharray="3 4" className="wu-pat__guide" />
			<line x1="0" x2={W} y1={y(12)} y2={y(12)} strokeDasharray="3 4" className="wu-pat__guide" />
			{marks}
		</svg>
	);
};

export default WarmupPattern;
